import { parseFrontMatter } from './frontMatter.ts';
import { escapeHtml, normalizeAnchorId, sanitizeHtml } from './html.ts';
import { parseRuby } from './ruby.ts';

// プリコンパイルされた正規表現定数
const WINDOWS_NEWLINE_RE = /\r\n/g;

// ブロックレベルの正規表現（1行単位で判定）
const BLOCK_NOVEL_ANCHOR_RE = /^<!--\s*novel-anchor\s*:\s*([^>]+?)\s*-->$/i;
const BLOCK_NOVEL_TOC_LINK_RE = /^<!--\s*novel-toc-link\s*:\s*([^|>]+?)\s*\|\s*([\s\S]*?)\s*-->$/i;
const BLOCK_HEADING_BODY_RE = /^##\s+本文$/;
const BLOCK_HEADING_PREFACE_RE = /^##\s+前書き$/;
const BLOCK_HEADING_AFTERWORD_RE = /^##\s+あとがき$/;
const BLOCK_HEADING_H1_RE = /^#\s+(.+)$/;
const BLOCK_HEADING_H2_RE = /^##\s+(.+)$/;
const BLOCK_HEADING_H3_RE = /^###\s+(.+)$/;
const BLOCK_DOUBLE_LINE_RE = /^<!--\s*[=-]{3,}\s*-->$/;
const BLOCK_IMAGE_RE = /^!\[([\s\S]*?)\]\(([\s\S]*?)\)$/;

// インライン装飾用の正規表現定数
const EMPHASIS_DOTS_RE = /《《([^》\n\r]+)》》/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;
const ITALIC_RE = /\*([^*]+)\*/g;
const STRIKETHROUGH_RE = /~~([^~]+)~~/g;

// 縦中横用の正規表現定数 (Lookbehind非対応環境でも安全に動作する互換パターン)
const TAG_SPLIT_RE = /(<[^>]+>)/;
const TCY_RUN_RE = /\d+|[０-９]+|[!?]+|[！？]+/g;

/**
 * 傍点、太字、斜体、取り消し線のインライン装飾を適用します。
 */
function applyInlineDecorations(text: string): string {
  if (!text.includes('*') && !text.includes('~') && !text.includes('《《')) return text;

  let result = text;
  if (result.includes('《《')) {
    result = result.replace(EMPHASIS_DOTS_RE, '<span class="novel-emphasis-dots">$1</span>');
  }
  if (result.includes('**')) {
    result = result.replace(BOLD_RE, '<strong class="novel-bold">$1</strong>');
  }
  if (result.includes('*')) {
    result = result.replace(ITALIC_RE, '<em class="novel-italic">$1</em>');
  }
  if (result.includes('~~')) {
    result = result.replace(STRIKETHROUGH_RE, '<del class="novel-strikethrough">$1</del>');
  }
  return result;
}

/**
 * 簡易的にMarkdownをHTMLへ変換します。小説用にスタイルクラスを付与します。
 */
export function parseMarkdownToHtml(markdown: string): string {
  // フロントマターが存在する場合は取り除く
  const { content: cleanMarkdown } = parseFrontMatter(markdown);

  // 改行コードを \n に統一して Windows の \r\n によるマッチ失敗を防ぐ
  const normalizedMarkdown = cleanMarkdown.replace(WINDOWS_NEWLINE_RE, '\n');

  // 1行単位で処理を行う（1パス化）
  const lines = normalizedMarkdown.split('\n');
  const parsedLines: string[] = [];
  let consecutiveEmptyLines = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      consecutiveEmptyLines++;
      continue;
    }

    const firstChar = trimmed[0];

    // --- ブロックレベルのパース (先頭文字で事前フィルタして高速化) ---

    if (firstChar === '<') {
      // 1. novel-anchor, novel-toc-link, 二重線
      if (trimmed.startsWith('<!--')) {
        let match = BLOCK_NOVEL_ANCHOR_RE.exec(trimmed);
        if (match) {
          const id = normalizeAnchorId(match[1]);
          parsedLines.push(`<span id="${id}" class="novel-anchor" aria-hidden="true"></span>`);
          consecutiveEmptyLines = 0;
          continue;
        }

        match = BLOCK_NOVEL_TOC_LINK_RE.exec(trimmed);
        if (match) {
          const id = normalizeAnchorId(match[1]);
          const title = escapeHtml(match[2].replace(/\s+/g, ' ').trim());
          const decorated = applyTateChuYoko(parseRuby(applyInlineDecorations(title)));
          parsedLines.push(
            `<a href="#${id}" class="novel-toc-link" data-section-link="${id}">${decorated}</a>`,
          );
          consecutiveEmptyLines = 0;
          continue;
        }

        if (BLOCK_DOUBLE_LINE_RE.test(trimmed)) {
          parsedLines.push('<div class="novel-double-line" aria-hidden="true"></div>');
          consecutiveEmptyLines = 0;
          continue;
        }
      } else if (/^<(?:h1|h2|h3|div|span|a)\b/i.test(trimmed)) {
        // HTMLタグが含まれている場合のフォールバック
        parsedLines.push(line);
        consecutiveEmptyLines = 0;
        continue;
      }
    } else if (firstChar === '#') {
      // 3. 特別な見出し（本文、前書き、あとがき） & 4. 通常の見出し
      if (BLOCK_HEADING_BODY_RE.test(trimmed)) {
        // 「## 本文」は表示しない
        consecutiveEmptyLines = 0;
        continue;
      }
      if (BLOCK_HEADING_PREFACE_RE.test(trimmed)) {
        parsedLines.push('<h2 class="novel-h2 novel-preface-title">前書き</h2>');
        consecutiveEmptyLines = 0;
        continue;
      }
      if (BLOCK_HEADING_AFTERWORD_RE.test(trimmed)) {
        parsedLines.push('<h2 class="novel-h2 novel-afterword-title">あとがき</h2>');
        consecutiveEmptyLines = 0;
        continue;
      }

      let match = BLOCK_HEADING_H1_RE.exec(trimmed);
      if (match) {
        parsedLines.push(
          `<h1 class="novel-h1">${applyTateChuYoko(parseRuby(applyInlineDecorations(match[1])))}</h1>`,
        );
        consecutiveEmptyLines = 0;
        continue;
      }
      match = BLOCK_HEADING_H2_RE.exec(trimmed);
      if (match) {
        parsedLines.push(
          `<h2 class="novel-h2">${applyTateChuYoko(parseRuby(applyInlineDecorations(match[1])))}</h2>`,
        );
        consecutiveEmptyLines = 0;
        continue;
      }
      match = BLOCK_HEADING_H3_RE.exec(trimmed);
      if (match) {
        parsedLines.push(
          `<h3 class="novel-h3">${applyTateChuYoko(parseRuby(applyInlineDecorations(match[1])))}</h3>`,
        );
        consecutiveEmptyLines = 0;
        continue;
      }
    } else if (firstChar === '!') {
      // 6. 画像
      const match = BLOCK_IMAGE_RE.exec(trimmed);
      if (match) {
        parsedLines.push(
          `<div class="novel-illust-container"><img src="${match[2]}" alt="${match[1]}" class="novel-illust" loading="lazy" /></div>`,
        );
        consecutiveEmptyLines = 0;
        continue;
      }
    }

    // --- 通常のテキスト行処理 ---

    // 直前の空行数によってギャップクラスを追加
    let gapClass = '';
    if (consecutiveEmptyLines === 1) {
      gapClass = ' novel-gap-1';
    } else if (consecutiveEmptyLines === 2) {
      gapClass = ' novel-gap-2';
    } else if (consecutiveEmptyLines >= 3) {
      gapClass = ' novel-gap-3';
    }

    // 各行をそのまま処理（ここで改行でスプリットする必要はない）
    const leadingIdeographicSpaces = line.match(/^　+/u)?.[0] ?? '';
    const normalizedLine = `${leadingIdeographicSpaces}${trimmed}`;
    const decorated = applyInlineDecorations(normalizedLine);
    const withRuby = parseRuby(decorated);
    const processedLine = applyTateChuYoko(withRuby);
    const innerLines = `<span class="novel-line">${processedLine}</span>`;

    // 地の文のインデント判定
    const startChar = normalizedLine.charAt(0);
    const needIndent = !/^[「『（(【〔［[“‘\u3000\s―—・]/.test(startChar);
    const pClass = `novel-p${needIndent ? ' indent-p' : ''}${gapClass}`;

    parsedLines.push(`<p class="${pClass}">${innerLines}</p>`);
    consecutiveEmptyLines = 0;
  }

  const resultHtml = parsedLines.join('\n');
  return sanitizeHtml(resultHtml);
}

/**
 * 縦書き時の自動縦中横 (2桁の半角・全角数字、および2文字の感嘆符・疑問符) を適用します。
 * HTMLタグの属性値を汚染しないよう、タグ外のテキスト部分にのみ適用します。
 * Lookbehind非対応の古いブラウザ環境でも構文エラーを起こさずに動作します。
 */
function applyTateChuYoko(text: string): string {
  if (!/[0-9０-９!?！？]/.test(text)) return text;

  const replaceRun = (str: string) =>
    str.replace(TCY_RUN_RE, (match) =>
      match.length === 2 ? `<span class="tate-chu-yoko">${match}</span>` : match,
    );

  if (!text.includes('<')) {
    return replaceRun(text);
  }

  const parts = text.split(TAG_SPLIT_RE);
  let res = '';
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0 && parts[i]) {
      res += replaceRun(parts[i]);
    } else {
      res += parts[i];
    }
  }
  return res;
}
